import React, { useEffect, useState, useRef } from "react";
import "../../../styles/desktop/CampoLuzes.css";
import { LightGroup } from "../../Classes/LightGroup";
import Ligado from "../../../assets/img/Ligado.png";
import Desligado from "../../../assets/img/Desligado.png";
import { getLuzesStatusFlex8, statusDasLuzesFlex8 } from "../../../API/basic/FeedBackLuzes";
import { sendCommandFlex8 } from "../../../API/basic/AcionamentoFlex8";
import LegendaAutomatica from "../LegendaAutomatica/LegendaAutomatica";
import { iluminacaoConfig } from "../../../configs/iluminação";

// Definição dos dados de todos os circuitos
const lightGroupsConfig = iluminacaoConfig
/**
 * Componente de Grupo de Luzes com Suporte a Módulos Específicos
 * @param {Object} props
 * @param {number} [props.startIndex=0] - Índice inicial (para compatibilidade)
 * @param {number} [props.endIndex=20] - Índice final (para compatibilidade)
 * @param {string[]} [props.modules] - Lista de módulos específicos (opcional)
 * @param {string} [props.pageTitle="Página 1"] - Título da página
 * @param {string} [props.pageId="1"] - ID da página para o acionarAll
 */

const GroupLightComponent = ({ 
  startIndex = 0, 
  endIndex = 20, 
  groupIds = [], 
  pageTitle = "Página 1",
  pageId = "1"
}) => {
  // Filtra os grupos visíveis com base nos IDs específicos ou nos índices fornecidos
  const visibleGroups = groupIds && groupIds.length > 0
    ? lightGroupsConfig.filter(group => groupIds.includes(group.id))
    : lightGroupsConfig.slice(startIndex, endIndex);

  // Inicialização dinâmica das refs
  const createInitialRefs = () => {
    const refs = {};
    visibleGroups.forEach(group => {
      refs[group.id] = React.createRef();
    });
    return refs;
  };

  // Inicialização dinâmica dos estados
  const createInitialStates = () => {
    const states = {};
    visibleGroups.forEach(group => {
      states[group.id] = false;
    });
    return states;
  };

  const groupRefs = useRef(createInitialRefs());
  const [imageStates, setImageStates] = useState(createInitialStates());
  const [loading, setLoading] = useState(true);
  const [acionarAllText, setAcionarAllText] = useState("Ligar");
  const [isBlocked, setIsBlocked] = useState(false);
  const [isSequencing, setIsSequencing] = useState(false); // Estado para controlar a animação

  useEffect(() => {
    const fetchData = () => {
      if (isBlocked) return;

      getLuzesStatusFlex8()
        .then(() => {
          console.log("Feedback de status das luzes:", statusDasLuzesFlex8);
          updateLightGroup(statusDasLuzesFlex8);
          setLoading(false);
        })
        .catch(error => {
          console.error("Erro ao buscar status das luzes:", error);
          setLoading(false);
        });
    };

    fetchData();
    const intervalId = setInterval(fetchData, 3000);
    return () => clearInterval(intervalId);
  }, [isBlocked]);

  const updateLightGroup = (data) => {
    if (!data || !Array.isArray(data)) {
      console.error("Dados inválidos recebidos para updateLightGroup.");
      return;
    }
    
    console.log("Status das luzes:", JSON.stringify(data));
    
    // Método mais dinâmico para atualizar os estados
    const newStates = { ...imageStates };
    
    visibleGroups.forEach(group => {
      const moduleIndex = parseInt(group.modulo) - 1;
      const idxIndex = parseInt(group.idx) - 1;
      
      if (data[moduleIndex] && data[moduleIndex].dados && data[moduleIndex].dados[idxIndex] !== undefined) {
        newStates[group.id] = data[moduleIndex].dados[idxIndex] === "ON";
      }
    });
    
    setImageStates(newStates);
    
    // Verifica se todas as luzes estão ligadas para definir o texto do botão
    const todasLuzesLigadas = Object.values(newStates).every(state => state === true);
    setAcionarAllText(todasLuzesLigadas ? "Desligar" : "Ligar");
  };

  const handleToggleLightGroup = (groupId, newImageState) => {
    setImageStates(prevState => ({ ...prevState, [groupId]: newImageState }));
  };

  const handleAcionarAll = async () => {
    // Determina a ação com base no estado atual das luzes
    const action = Object.values(imageStates).some(state => state === false);
    
    try {
      // Indica que uma sequência de animação está em andamento
      setIsSequencing(true);
      
      // Bloqueia o polling temporariamente para evitar que sobrescreva nossas animações
      setIsBlocked(true);
      
      // Processamos com base nos grupos visíveis
      if (action) {
        // LIGANDO TODAS AS LUZES SEQUENCIALMENTE
        for (const group of visibleGroups) {
          const groupId = group.id;
          
          // Atualiza o estado desta luz específica
          setImageStates(prevState => ({
            ...prevState,
            [groupId]: true
          }));
          
          // Envia o comando para o servidor para esta luz específica
          await sendCommandFlex8(group.descricao, { 
            modulo: group.modulo, 
            idx: group.idx, 
            status: "ON" 
          });
          
          // Aguarda um delay mais longo antes de acender a próxima luz
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      } else {
        // DESLIGANDO TODAS AS LUZES SEQUENCIALMENTE (na ordem inversa)
        for (let i = visibleGroups.length - 1; i >= 0; i--) {
          const group = visibleGroups[i];
          const groupId = group.id;
          
          // Atualiza o estado desta luz específica
          setImageStates(prevState => ({
            ...prevState,
            [groupId]: false
          }));
          
          // Envia o comando para o servidor para esta luz específica
          await sendCommandFlex8(group.descricao, { 
            modulo: group.modulo, 
            idx: group.idx, 
            status: "OFF" 
          });
          
          // Aguarda um delay mais longo antes de apagar a próxima luz
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      // Nota: Não chamamos acionarAll() aqui, porque queremos apenas acionar as luzes específicas
      // em vez de todas as luzes da página
      
      // Atualiza o texto do botão
      setAcionarAllText(action ? "Desligar" : "Ligar");
      
      // Mantém o bloqueio por mais tempo para garantir que o servidor tenha tempo de processar
      setTimeout(() => {
        setIsBlocked(false);
        setIsSequencing(false); // Finaliza o estado de sequenciamento
      }, 5000); // 5 segundos de espera após a conclusão
      
    } catch (error) {
      console.error("Erro ao acionar todas as luzes:", error);
      // Em caso de erro, desbloqueamos o polling e o sequenciamento
      setIsBlocked(false);
      setIsSequencing(false);
    }
  };

  return (
    <>
      <div className="group" id={`campoGroup-pagina${pageId}`}>

        {/* Renderização dinâmica dos LightGroups */}
        {visibleGroups.map(group => {
          switch(group.lightType) {
            case "normal":
              return group.multipleInstances?.map((instance, index) => (
                <LightGroup
                  key={`${group.id}-${index}`}
                  className="light_button"
                  ref={index === 0 ? groupRefs.current[group.id] : undefined}
                  groupId={`${group.id}-${index}`}
                  descricao={group.descricao}
                  imageUrl={imageStates[group.id] ? Ligado : Desligado}
                  onCommand={{ modulo: group.modulo, idx: group.idx, status: "ON" }}
                  offCommand={{ modulo: group.modulo, idx: group.idx, status: "OFF" }}
                  x={instance.x}
                  y={instance.y}
                  width={instance.width}
                  height={instance.height}
                  circleNumber={instance.circleNumber}
                  imageState={imageStates[group.id]}
                  onToggleLightGroup={(id, state) => handleToggleLightGroup(group.id, state)}/>
              )) || (
                // Caso não tenha multipleInstances, renderiza um único
                <LightGroup
                  key={group.id}
                  className="light_button"
                  ref={groupRefs.current[group.id]}
                  groupId={group.id}
                  descricao={group.descricao}
                  imageUrl={imageStates[group.id] ? Ligado : Desligado}
                  onCommand={{ modulo: group.modulo, idx: group.idx, status: "ON" }}
                  offCommand={{ modulo: group.modulo, idx: group.idx, status: "OFF" }}
                  x={group.x}
                  y={group.y}
                  width={group.width}
                  height={group.height}
                  circleNumber={group.circleNumber}
                  imageState={imageStates[group.id]}
                  onToggleLightGroup={handleToggleLightGroup}/>);

            case "ledStrip":
              return (
                <LightGroup
                  key={group.id}
                  className="light_button"
                  ref={groupRefs.current[group.id]}
                  groupId={group.id}
                  descricao={group.descricao}
                  imageUrl={imageStates[group.id] ? Ligado : Desligado}
                  onCommand={{ modulo: group.modulo, idx: group.idx, status: "ON" }}
                  offCommand={{ modulo: group.modulo, idx: group.idx, status: "OFF" }}
                  x={group.x}
                  y={group.y}
                  width={group.width}
                  height={group.height}
                  imageState={imageStates[group.id]}
                  onToggleLightGroup={handleToggleLightGroup}
                  isLedStrip={true}
                  ledPath={group.ledPath}
                  ledColor={group.ledColor}
                  ledMode={group.ledMode}
                  ledStrokeWidth={group.ledStrokeWidth}/>);

            case "mixed":
              return (
                <>
                  {/* Renderiza luzes normais */}
                  {group.normalLights?.map((light, index) => (
                    <LightGroup
                      key={`${group.id}-normal-${index}`}
                      className="light_button"
                      ref={index === 0 ? groupRefs.current[group.id] : undefined}
                      groupId={`${group.id}-normal-${index}`}
                      descricao={group.descricao}
                      imageUrl={imageStates[group.id] ? Ligado : Desligado}
                      onCommand={{ modulo: group.modulo, idx: group.idx, status: "ON" }}
                      offCommand={{ modulo: group.modulo, idx: group.idx, status: "OFF" }}
                      x={light.x}
                      y={light.y}
                      width={light.width}
                      height={light.height}
                      circleNumber={light.circleNumber}
                      imageState={imageStates[group.id]}
                      onToggleLightGroup={(id, state) => handleToggleLightGroup(group.id, state)}/>))}

                  {/* Renderiza fitas LED */}
                  {group.ledStrips?.map((strip, index) => (
                    <LightGroup
                      key={`${group.id}-led-${index}`}
                      className="light_button"
                      groupId={`${group.id}-led-${index}`}
                      descricao={group.descricao}
                      imageUrl={imageStates[group.id] ? Ligado : Desligado}
                      onCommand={{ modulo: group.modulo, idx: group.idx, status: "ON" }}
                      offCommand={{ modulo: group.modulo, idx: group.idx, status: "OFF" }}
                      x={strip.x}
                      y={strip.y}
                      width={strip.width}
                      height={strip.height}
                      imageState={imageStates[group.id]}
                      onToggleLightGroup={(id, state) => handleToggleLightGroup(group.id, state)}
                      isLedStrip={true}
                      ledPath={strip.ledPath}
                      ledColor={strip.ledColor}
                      ledMode={strip.ledMode}
                      ledStrokeWidth={strip.ledStrokeWidth}/>))}
                </>
              );

            default:
              // Para compatibilidade com a configuração existente
              return (
                <LightGroup
                  key={group.id}
                  className="light_button"
                  ref={groupRefs.current[group.id]}
                  groupId={group.id}
                  descricao={group.descricao}
                  imageUrl={imageStates[group.id] ? Ligado : Desligado}
                  onCommand={{ modulo: group.modulo, idx: group.idx, status: "ON" }}
                  offCommand={{ modulo: group.modulo, idx: group.idx, status: "OFF" }}
                  x={group.x}
                  y={group.y}
                  width={group.width}
                  height={group.height}
                  circleNumber={group.circleNumber}
                  imageState={imageStates[group.id]}
                  onToggleLightGroup={handleToggleLightGroup}/>);
          }
        })}


        {/* Legenda de acionamentos */}
        <LegendaAutomatica 
          groupRefs={groupRefs} 
          imageStates={imageStates} 
          handleToggleLightGroup={handleToggleLightGroup}
          visibleGroups={visibleGroups}
          sendCommand={sendCommandFlex8}
          className="campo-clickable-element"
        />

        <button 
          disabled={isSequencing} 
          className={`bntAcionarAll${acionarAllText === 'Desligar' ? '-ativo' : ''}`} 
          onClick={handleAcionarAll}
        >
          {isSequencing ? "Aguarde..." : `${acionarAllText} luzes`}
        </button>
      </div>
    </>
  );
};

export default React.memo(GroupLightComponent);